import {
  OWNER_ACTIVATION_ALLOWED_PROPERTY_KEYS,
  OWNER_ACTIVATION_EVENT_NAMES,
  type AnalyticsProvider,
  type OwnerActivationAnalytics,
  type OwnerActivationAnalyticsEventMap,
  type OwnerActivationAnalyticsEventName,
  type OwnerActivationAnalyticsPlatform,
} from './ownerActivationAnalyticsTypes';

/**
 * Provider-independent analytics core shared by the native and web adapters.
 *
 * Responsibilities:
 * - validate the event name against the typed contract;
 * - strip forbidden/unknown properties and `undefined` values;
 * - inject stable context (onboarding_version, platform);
 * - isolate synchronous and asynchronous provider failures;
 * - never block or alter caller behavior.
 */

const KNOWN_EVENTS = new Set<string>(OWNER_ACTIVATION_EVENT_NAMES);

type Primitive = string | number | boolean;

function isPrimitive(value: unknown): value is Primitive {
  return (
    typeof value === 'string' ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    typeof value === 'boolean'
  );
}

/** Free-text guard: allowed string values in this contract are short enums. */
const MAX_STRING_LENGTH = 64;

export function sanitizeOwnerActivationProperties(
  properties: unknown,
  platform: OwnerActivationAnalyticsPlatform,
): Record<string, Primitive> {
  const sanitized: Record<string, Primitive> = {
    onboarding_version: 1,
    platform,
  };
  if (properties && typeof properties === 'object') {
    for (const [key, value] of Object.entries(
      properties as Record<string, unknown>,
    )) {
      if (!OWNER_ACTIVATION_ALLOWED_PROPERTY_KEYS.has(key)) continue;
      if (value === undefined || value === null) continue;
      if (!isPrimitive(value)) continue;
      if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
        continue;
      }
      sanitized[key] = value;
    }
  }
  // Context injected by the adapter always wins over caller-supplied values.
  sanitized.onboarding_version = 1;
  sanitized.platform = platform;
  return sanitized;
}

export function createOwnerActivationAnalytics(
  provider: AnalyticsProvider | null,
  platform: OwnerActivationAnalyticsPlatform,
  options?: { providerDeliversRemotely?: boolean },
): OwnerActivationAnalytics {
  const deliversRemotely =
    provider !== null && options?.providerDeliversRemotely === true;

  return {
    providerDeliversRemotely: deliversRemotely,
    track<E extends OwnerActivationAnalyticsEventName>(
      event: E,
      properties: OwnerActivationAnalyticsEventMap[E],
    ): void {
      try {
        if (!KNOWN_EVENTS.has(event)) return;
        if (!provider) return;
        const sanitized = sanitizeOwnerActivationProperties(
          properties,
          platform,
        );
        const result = provider.logEvent(event, sanitized);
        // Isolate async provider rejection.
        if (
          result &&
          typeof (result as PromiseLike<unknown>).then === 'function'
        ) {
          void Promise.resolve(result).catch(() => {});
        }
      } catch {
        // Provider failure must never block or alter user behavior.
      }
    },
  };
}
